import "./styles/main.css"
import * as Sentry from "@sentry/vue"
import { until } from "@vueuse/core"
import { createHead } from "@vueuse/head"
import FloatingVue from "floating-vue"
import type { ComponentPublicInstance } from "vue"
import { createApp } from "vue"
import type { RouteRecordRaw } from "vue-router"
import { createRouter, createWebHistory } from "vue-router"
import { routes as pageRoutes } from "vue-router/auto-routes"
import Popper from "vue3-popper"
import App from "./App.vue"
import api from "./utils/api"
import { initAuth } from "./utils/auth"
import { isProd } from "./utils/environment"
import useLiveChat from "./utils/live-chat"
import "floating-vue/dist/style.css"
import Gleap from "gleap"
import Toast, { POSITION } from "vue-toastification"
import "vue-toastification/dist/index.css"
import { createPinia } from "pinia"
import VueConfetti from "vue-confetti"
import { hasConsented } from "./utils/cookie-consent"
import {
    filterHandledDynamicImportEvent,
    installDynamicImportRecovery,
} from "./utils/dynamic-import-recovery"
import { trackGoogleAnalyticsEvent } from "./utils/google-analytics"
import { initializePosthog, posthog } from "./utils/posthog"

const app = createApp(App)

// Initialise Sentry as early as possible after `createApp`. DSN is bundled
// at build time from `VITE_FRONTEND_SENTRY_DSN`. CI sources that stable build
// variable from /dashboard/DASHBOARD_FRONTEND_SENTRY_DSN, with a temporary
// fallback to the legacy generic Phase key during migration.
// See docs/observability.md §2 + §3.
//
// Wrapped in try/catch: a Sentry init failure (missing build-time DSN,
// malformed DSN, browser API quirk) MUST NOT prevent the Vue app from
// mounting — a blank page is worse than missing error telemetry.
try {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_FRONTEND_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT_NAME ?? "development",
        release: import.meta.env.VITE_COMMIT_SHA,
        // Bugsink-compat: errors only — no traces, no replays.
        // https://www.bugsink.com/docs/sdk-recommendations/
        tracesSampleRate: 0,
        beforeSend: filterHandledDynamicImportEvent,
    })
} catch (err) {
    console.warn("[sentry] init threw; continuing without Sentry.", err)
}
app.component("Popper", Popper)
app.use(FloatingVue)
app.use(Toast, {
    position: POSITION.TOP_RIGHT,
})
app.use(VueConfetti)
app.provide("confetti", app.config.globalProperties.$confetti)

const pinia = createPinia()
app.use(pinia)

const routes: RouteRecordRaw[] = [...pageRoutes, { path: "/", redirect: "/campaigns" }]

export const history = createWebHistory()
export const router = createRouter({ history, routes })

router.afterEach(() => {
    const url = new URL(window.location.href)

    window.analytics?.page()

    posthog.capture("page_view", {
        path: url.pathname,
        search: url.search,
        url: url.href,
        referrer: document.referrer,
    })

    Gleap.trackEvent("trackPage", {
        page: url.pathname,
        url: url.href,
        referrer: document.referrer,
    })

    // check cookie permissions before sending data to pixels
    if (hasConsented("marketing")) {
        window.fbq?.("track", "PageView")
    }

    trackGoogleAnalyticsEvent("page_view", {
        page_title: document.title,
        page_location: window.location,
    })
})

installDynamicImportRecovery(router, {
    currentHref: () => window.location.href,
    online: () => window.navigator.onLine,
    fetchFn: window.fetch.bind(window),
    storage: window.sessionStorage,
    assign: (targetPath) => window.location.assign(targetPath),
    captureException: (exception, hint) => Sentry.captureException(exception, hint),
    release: import.meta.env.VITE_COMMIT_SHA,
})
app.use(router)

const head = createHead()
app.use(head)

async function main() {
    Gleap.initialize("vPRkPJFdtCTQfWrDXK8wgi7L1i8ZZBuE")

    // Initializes only when analytics consent already exists. If consent is
    // granted later, consent.ts initializes this same singleton and flushes
    // the authenticated identity without requiring a reload.
    initializePosthog()

    // analytics
    until(() => window.analytics !== undefined)
        .toBeTruthy()
        .then(() => {
            const SEGMENT_WRITE_KEY = isProd()
                ? "a0qPSNtsZxWudyFpa2oD7t5lPgr7B7HC" // production
                : "NUw9I4Cc7pmUkxmHHpwJtIJawMkB0ZlI" // staging

            window.analytics!.load(SEGMENT_WRITE_KEY)

            window.analytics!.page()
        })

    app.config.errorHandler = (
        error: any,
        instance: ComponentPublicInstance | null,
        info: string,
    ) => {
        console.error("App Error", error)
    }

    app.config.warnHandler = (
        msg: string,
        instance: ComponentPublicInstance | null,
        trace: string,
    ) => {
        console.warn("App Warning", msg, trace)
    }

    const publicRoutes = ["/privacy", "/terms"]
    if (!publicRoutes.includes(window.location.pathname)) {
        await initAuth(api)
    }
    app.mount("#app")

    useLiveChat()
}

main()
